import React from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../../store/themeStore';
import StudioBestTimeToPost from '../studio/StudioBestTimeToPost';
import type { ScheduleConfig } from './types';

interface Props {
  scheduleConfig: ScheduleConfig;
  setScheduleConfig: React.Dispatch<React.SetStateAction<ScheduleConfig>>;
}

export const SchedulePanel: React.FC<Props> = ({ scheduleConfig, setScheduleConfig }) => {
  const { theme } = useThemeStore();
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <span className="block text-sm font-semibold text-gray-900 mb-3">{t('compose_schedule', 'Schedule')}</span>
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setScheduleConfig({ scheduleType: 'now' })}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all border ${
            scheduleConfig.scheduleType === 'now'
              ? 'border-transparent text-white'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
          style={scheduleConfig.scheduleType === 'now' ? { backgroundColor: theme.hex.primary } : undefined}
        >
          {t('compose_post_now', 'Post Now')}
        </button>
        <button
          onClick={() => setScheduleConfig({ scheduleType: 'later' })}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all border ${
            scheduleConfig.scheduleType === 'later'
              ? 'border-transparent text-white'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
          style={scheduleConfig.scheduleType === 'later' ? { backgroundColor: theme.hex.primary } : undefined}
        >
          {t('compose_schedule_button', 'Schedule')}
        </button>
      </div>

      {scheduleConfig.scheduleType === 'later' && (
        <div className="space-y-2.5 pt-2 border-t border-gray-100">
          {/* Analytics-driven best-time hint. Click-to-apply fills
              the date + time inputs below so users don't have to
              guess. The hook hits `analyticsAPI.bestTimes()` which
              caches for 5 min across both Composer and Studio. */}
          <StudioBestTimeToPost
            visible
            onPick={(date, time) => {
              const [hours, minutes] = time.split(':');
              const next = new Date(`${date}T00:00:00`);
              next.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
              setScheduleConfig((prev) => ({ ...prev, scheduledAt: next }));
            }}
          />

          <div>
            <label htmlFor="schedule-date" className="block text-xs text-gray-500 mb-1">{t('compose_date', 'Date')}</label>
            <input
              id="schedule-date"
              type="date"
              value={scheduleConfig.scheduledAt ? scheduleConfig.scheduledAt.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const date = new Date(e.target.value);
                setScheduleConfig((prev) => ({ ...prev, scheduledAt: date }));
              }}
              min={new Date().toISOString().split('T')[0]}
              style={{ outlineColor: theme.hex.primaryRing }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2"
            />
          </div>
          <div>
            <label htmlFor="schedule-time" className="block text-xs text-gray-500 mb-1">{t('compose_time', 'Time')}</label>
            <input
              id="schedule-time"
              type="time"
              value={scheduleConfig.scheduledAt ? scheduleConfig.scheduledAt.toTimeString().slice(0, 5) : ''}
              onChange={(e) => {
                const [hours, minutes] = e.target.value.split(':');
                setScheduleConfig((prev) => {
                  const date = prev.scheduledAt || new Date();
                  date.setHours(parseInt(hours), parseInt(minutes));
                  return { ...prev, scheduledAt: date };
                });
              }}
              style={{ outlineColor: theme.hex.primaryRing }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2"
            />
          </div>
        </div>
      )}
    </div>
  );
};
